import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "react-query";
import { AlertTriangle, Inbox } from "lucide-react";
import toast from "react-hot-toast";
import { apiService, extractApiError } from "../services/api";
import { LicenseInboxItem } from "../types";
import LoadingSpinner from "../components/LoadingSpinner";
import MessageCard from "../components/MessageCard";

export default function ApprovalsPage() {
  const { data, isLoading, isError, error } = useQuery<LicenseInboxItem[]>(
    "license-inbox",
    async () => {
      const res = await apiService.getLicenseInbox();
      if (res.data?.success && res.data.data) {
        return (res.data.data as { items: LicenseInboxItem[] }).items ?? [];
      }
      return [];
    },
    { retry: false },
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <LoadingSpinner />
      </div>
    );
  }

  if (isError) {
    const status = (error as { response?: { status?: number } })?.response
      ?.status;
    if (status === 403) {
      return (
        <div className="space-y-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            License approvals
          </h1>
          <MessageCard
            icon={AlertTriangle}
            title="Not configured as approver"
            tone="warning"
          >
            {
              "You're not configured as a cost-center approver. If you think this is wrong, contact the platform team."
            }
          </MessageCard>
        </div>
      );
    }
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          License approvals
        </h1>
        <MessageCard
          icon={AlertTriangle}
          title="Something went wrong"
          tone="danger"
        >
          Could not load the approvals inbox. Please try again later.
        </MessageCard>
      </div>
    );
  }

  const items = data ?? [];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
        License approvals
      </h1>
      <InboxTable items={items} />
    </div>
  );
}

interface InboxTableProps {
  items: LicenseInboxItem[];
}

function InboxTable({ items }: InboxTableProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedDomain = searchParams.get("domain") ?? null;
  const queryClient = useQueryClient();
  const [deciding, setDeciding] = useState<string | null>(null);

  const domains = useMemo(() => {
    const seen = new Set<string>();
    for (const item of items) {
      seen.add(item.domain);
    }
    return Array.from(seen).sort();
  }, [items]);

  const filtered = useMemo(() => {
    if (!selectedDomain) return items;
    return items.filter((item) => item.domain === selectedDomain);
  }, [items, selectedDomain]);

  async function handleDecide(id: string, decision: "approve" | "reject") {
    setDeciding(id);
    try {
      await apiService.decideLicenseRequest(id, decision);
      toast.success(
        decision === "approve" ? "Request approved" : "Request rejected",
      );
      await queryClient.invalidateQueries("license-inbox");
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response
        ?.status;
      if (status === 409) {
        toast.error(
          extractApiError(err, "Already decided by another approver"),
        );
        await queryClient.invalidateQueries("license-inbox");
      } else {
        toast.error(extractApiError(err, "Failed to record decision"));
      }
    } finally {
      setDeciding(null);
    }
  }

  if (items.length === 0) {
    return (
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Pending decisions (0)
        </h2>
        <MessageCard
          icon={Inbox}
          title="No pending license requests."
          tone="default"
        >
          {""}
        </MessageCard>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Pending decisions ({filtered.length})
        </h2>
        {domains.length > 1 && (
          <select
            className="input w-auto"
            value={selectedDomain ?? ""}
            onChange={(e) => {
              const val = e.target.value;
              if (val) {
                setSearchParams({ domain: val });
              } else {
                setSearchParams({});
              }
            }}
          >
            <option value="">All domains</option>
            {domains.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        )}
      </div>
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Domain
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Requester
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Tier
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Submitted
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {filtered.map((item) => (
              <tr key={item.id}>
                <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                  {item.domain}
                </td>
                <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                  {item.requesterEmail}
                </td>
                <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                  ${item.requestedTier}
                </td>
                <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                  {new Date(item.createdAt).toLocaleString()}
                </td>
                <td className="px-6 py-4 text-sm text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      className="btn btn-outline text-xs px-3 py-1"
                      disabled={deciding === item.id}
                      onClick={() => handleDecide(item.id, "reject")}
                    >
                      Reject
                    </button>
                    <button
                      className="btn btn-primary text-xs px-3 py-1"
                      disabled={deciding === item.id}
                      onClick={() => handleDecide(item.id, "approve")}
                    >
                      Approve
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
